import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { configureAnalytics, track, type ConsentMode } from '../../../utils/analytics'
import { withSupportMessage } from '../../../utils/errorMessage'
import { buildConsentConfigResponse, type ConsentConfigPayload } from '../consentConfig'
import { getSecondaryItems } from '../consentOptions/consentOptionsUtils'
import type { SecondaryConsentConfigError } from './useSecondaryConsentConfig'

/**
 * Builds the secondary consent config (configId/version + items) from the dedicated
 * `/config/consents/secondary` payload, and owns the consent-flow-started analytics.
 * Primary consent copy/errors are handled separately by `useConsentLanguage`.
 */
export function useConsentConfig(
  secondaryConsentPayload: ConsentConfigPayload | null,
  secondaryConsentLoading: boolean,
  secondaryConsentError: SecondaryConsentConfigError | null
) {
  const intl = useIntl()
  const [flowStartedAt, setFlowStartedAt] = useState<number | null>(null)
  const flowStartedRef = useRef(false)

  const consentConfig = useMemo(
    () => (secondaryConsentPayload ? buildConsentConfigResponse(secondaryConsentPayload, intl.locale) : null),
    [secondaryConsentPayload, intl.locale]
  )

  const consentConfigLoading = secondaryConsentLoading

  const consentConfigError = useMemo(
    () =>
      secondaryConsentError
        ? withSupportMessage(
            intl,
            intl.formatMessage({
              id: 'consentOptions.error.couldNotLoadOptions',
              defaultMessage: "We couldn't load consent options.",
            })
          )
        : null,
    [secondaryConsentError, intl]
  )

  useEffect(() => {
    if (secondaryConsentError) {
      track('consent.load_error', { resource: 'secondary_config', status: secondaryConsentError })
    }
  }, [secondaryConsentError])

  useEffect(() => {
    if (consentConfigLoading || !consentConfig || flowStartedRef.current) return
    const modeParam = new URLSearchParams(window.location.search).get('mode')
    const mode: ConsentMode = modeParam === 'B' ? 'B' : 'A'
    configureAnalytics({ flowVersion: consentConfig.configVersionId, mode })
    flowStartedRef.current = true
    setFlowStartedAt(Date.now())
    track('consent.flow_started', {})
  }, [consentConfigLoading, consentConfig])

  const secondaryItems = useMemo(() => (consentConfig ? getSecondaryItems(consentConfig) : []), [consentConfig])

  return {
    consentConfig,
    consentConfigLoading,
    consentConfigError,
    flowStartedAt,
    secondaryItems,
  }
}
